<template>
  <v-data-table-server
    :headers="headers"
    :items="items"
    :items-per-page="modelValue.itemsPerPage"
    :mobile-breakpoint="0"
    :sort-by="sortByV3"
    :page="modelValue.page"
    class="dashboard-table"
    :loading="filterLoading"
    show-select
    item-value="_key"
    :items-length="serverItemsLength"
    :hide-default-header="$vuetify.display.mdAndDown"
    :model-value="selectedKeys"
    :items-per-page-options="modelValue.itemsPerPageOptions.map(item => item.value)"
    @update:page="updateModel({ page: $event })"
    @update:sort-by="updateSortBy"
    @update:items-per-page="updateModel({ itemsPerPage: $event })"
    @update:model-value="$emit('update:selectedKeys', $event)"
  >
    <template #item="{ item, index }">
      <OpenInvoiceMobileCard
        v-if="$vuetify.display.mdAndDown"
        :item="item"
        :active-account-id="activeAccountId"
        :class="index === 0 ? 'mt-0' : 'mt-2'"
        :selected="selectedKeys.includes(item._key)"
        @click:select="changeSelectHandler(item._key)"
      />
      <tr v-else class="sc-broker-ranking-broker">
        <td class="sc-data-list-select">
          <v-checkbox
            hide-details
            class="mt-0 pt-0"
            :model-value="selectedKeys.includes(item._key)"
            @update:model-value="changeSelectHandler(item._key)"
          />
        </td>
        <td class="sc-data-list-select" style="width: 80px">
          <CommonChip class="sc-nbr sc-chip--dark">
            {{ index + 1 }}
          </CommonChip>
        </td>
        <td class="sc-data-list-select" style="width: 80px">
          <span>{{ item.invoice_number }}</span>
        </td>
        <td class="sc-data-list" style="min-width: 300px">
          <router-link
            class="font-weight-bold text-primary text-decoration-none"
            :to="getAccountRoute({ name: 'deal', params: { id: item.deal._key }})"
          >
            {{ item.deal.name }}
          </router-link>
        </td>
        <td class="sc-data-list" style="min-width: 200px">
          <Label :value="getBrokers(item.deal)" />
        </td>
        <td class="sc-data-list">
          <Label :value="getCompanyNames(item.buyer_tenant)" />
        </td>
        <td class="sc-data-list">
          <Label :value="getCompanyNames(item.seller_landlord)" />
        </td>
        <td class="sc-data-list">
          <Label :value="item.due_date ? $filters.formatDate(item.due_date) : null" />
        </td>
        <td class="sc-data-list">
          {{ $filters.formatCurrency(item.amount) }}
        </td>
        <td class="sc-data-list">
          {{ $filters.formatCurrency(item.amount_paid) }}
        </td>
        <td class="sc-data-list">
          {{ $filters.formatCurrency(item.amount_outstanding) }}
        </td>
        <td class="sc-data-list">
          <span :style="`color: ${item.color}`">{{ $filters.capitalize(item.status) }}</span>
        </td>
        <td class="sc-data-list" style="min-width: 110px">
          <Label :value="item.diffLabel" />
        </td>
        <td class="sc-data-list">
          <Label :value="item.daysDiff" />
        </td>
      </tr>
    </template>
  </v-data-table-server>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue';
import { capitalize } from 'lodash-es';

import { IHeader, ITableOptions } from '@/bundles/BaseTable/interfaces';
import { AccountLayoutMixin } from '@/bundles/App/mixins/accountMixin';
import i18n from '@/bundles/Localization';
import { LocalizationEnum } from '@/bundles/Localization/enum/LocalizationEnum';

import Label from '@/bundles/Common/components/Label.vue';
import CommonChip from '@/bundles/Common/components/chips/CommonChip.vue';
import OpenInvoiceMobileCard from '@/bundles/Dashboard/components/OpenInvoiceMobileCard.vue';

interface IOpenInvoicesTableData {
  headers?: IHeader[];
}

const companyNameSortFunc = (a: { name: string }[], b: { name: string }[]) => {
  const aJoin = a.map(item => item.name).join(', ');
  const bJoin = b.map(item => item.name).join(', ');

  if (aJoin && bJoin) {
    return aJoin.localeCompare(bJoin);
  }

  return 0;
};

const headers = [
  { title: '#', key: '#', sortable: false },
  { title: 'Invoice No.', key: 'invoice_number', sortable: true, class: '', icon: 'number' },
  { title: 'Deal Name', key: 'deal.name', sortable: true, class: '', icon: 'string' },
  { title: `${capitalize(i18n.tc(LocalizationEnum.broker))}(s)`, key: 'broker', sortable: false, class: '', icon: 'string' },
  { title: 'Buyer/Tenant', key: 'buyer_tenant', sortable: true, class: '', icon: 'string', sort: companyNameSortFunc },
  { title: 'Seller/Landlord', key: 'seller_landlord', sortable: true, class: '', icon: 'string', sort: companyNameSortFunc },
  { title: 'Due Date', key: 'due_date', sortable: true, class: '', icon: 'date' },
  { title: 'Invoice Amount ($)', key: 'amount', sortable: true, class: '', icon: 'number' },
  { title: 'Amount Paid ($)', key: 'amount_paid', sortable: true, class: '', icon: 'number' },
  { title: 'Outstanding ($)', key: 'amount_outstanding', sortable: true, class: '', icon: 'number' },
  { title: 'Payment Status', key: 'status', sortable: true, class: '', icon: 'string' },
  { title: 'Aging', key: 'diffLabel', sortable: true, class: '', icon: 'number' },
  { title: '# Days Aged', key: 'daysDiff', sortable: true, class: '', icon: 'number' }
]

export default defineComponent({
  name: 'OpenInvoicesTable',

  components: {
    OpenInvoiceMobileCard,
    CommonChip,
    Label
  },
  mixins: [AccountLayoutMixin],

  props: {
    modelValue: {
      type: Object as PropType<ITableOptions>,
      required: true
    },
    items: {
      type: Array as PropType<any[]>,
      default: () => []
    },
    filterLoading: Boolean,
    selectedKeys: {
      type: Array as PropType<string[]>,
      default: () => [],
    },
    serverItemsLength: {
      type: Number,
      default: -1,
    }
  },

  emits: ['update:modelValue', 'update:selectedKeys'],

  data: (): IOpenInvoicesTableData => ({}),

  computed: {
    sortByV3 (): { key: string; order: 'asc' | 'desc' }[] {
      const sortBy = this.modelValue.sortBy || [];
      const sortDesc = this.modelValue.sortDesc || [];

      return sortBy.map((key: string, index: number) => ({
        key,
        order: sortDesc[index] ? 'desc' : 'asc',
      }));
    },
  },

  created () {
    this.headers = headers;
  },

  methods: {
    getCompanyNames (contacts: { name: string }[]): string {
      return contacts.map(item => item.name).join(', ') || '';
    },
    getBrokers (deal) {
      return deal.brokers.map(item => `${item.first_name} ${item.last_name}`).join(', ');
    },
    updateModel (model: Partial<ITableOptions>) {
      this.$emit('update:modelValue', { ...this.modelValue, ...model });
    },
    updateSortBy (sortBy: { key: string; order: 'asc' | 'desc' }[]) {
      this.updateModel({
        sortBy: sortBy.map((item) => item.key),
        sortDesc: sortBy.map((item) => item.order === 'desc'),
      });
    },
    // Rows are drawn by the `#item` slot with their own checkbox, so the table's
    // own model only changes from its header checkbox: Vuetify 3 has no
    // `toggle-select-all` event; the header writes the page's keys (`item-value`)
    // into the model instead.
    changeSelectHandler (key: string) {
      const selectedKeys = this.selectedKeys;

      const payload = selectedKeys.includes(key) ? selectedKeys.filter(item => item !== key) : [...selectedKeys, key];
      this.$emit('update:selectedKeys', payload);
    },
  }
})
</script>
